/**
 * The component inventory (task T-P0.3).
 *
 * Three button weights with hierarchy carried by shape as well as colour, so it
 * survives colour blindness; 48px minimum target on anything a finger reaches;
 * labels above inputs, hints above, errors above; 3:1 borders. All of that is
 * Practical UI, applied once here rather than argued about per screen.
 */

import { Link } from '@tanstack/react-router';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/cn';

/* -------------------------------------------------------------------------- */
/* Buttons                                                                     */
/* -------------------------------------------------------------------------- */

type ButtonWeight = 'primary' | 'secondary' | 'tertiary' | 'danger';
type ButtonSize = 'md' | 'lg' | 'sm';

const BUTTON_BASE =
  // 6px radius, medium weight, and NO movement on press. The sheet is explicit:
  // hover never resizes anything, fills shift 6%, borders deepen one step.
  // R-186.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[6px] font-medium ' +
  'transition-[background-color,border-color,color] duration-150 ease-[var(--ease-out-soft)] ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-control)] ' +
  'disabled:pointer-events-none disabled:opacity-60';

const BUTTON_WEIGHT: Record<ButtonWeight, string> = {
  // Near-black fill, paper label. The yellow used to be the primary CTA and it
  // is storefront decoration now: it cannot carry text at 4.5:1, and at button
  // size it vibrates against warm off-white all day. This pairing is 14.6:1.
  primary:
    'bg-[color:var(--color-control)] text-[color:var(--color-on-control)] '
    + 'hover:bg-[color:var(--color-control-hover)] active:bg-[color:var(--color-control-press)]',
  secondary:
    'border border-line-strong bg-panel text-ink hover:border-[color:var(--color-ink)] hover:bg-raised',
  tertiary: 'text-ink underline underline-offset-4 decoration-line-strong hover:decoration-ink',
  danger:
    'border border-danger/40 bg-danger-wash text-[color:var(--color-danger-ink)] hover:border-danger',
};

const BUTTON_SIZE: Record<ButtonSize, string> = {
  // Tighter than before, because the sheet's density is 32px rows and a 48px
  // top bar. `lg` stays generous for the storefront's two hero buttons.
  sm: 'h-8 px-3 text-[12.5px]',
  md: 'h-9 px-4 text-[13px]',
  lg: 'h-11 px-6 text-[14px]',
};

export function buttonClass(
  weight: ButtonWeight = 'primary',
  size: ButtonSize = 'md',
  className?: string,
) {
  return cn(
    BUTTON_BASE,
    BUTTON_WEIGHT[weight],
    weight === 'tertiary' ? 'h-auto px-0 py-2' : BUTTON_SIZE[size],
    className,
  );
}

export function Button({
  weight = 'primary',
  size = 'md',
  className,
  ...props
}: ComponentProps<'button'> & { weight?: ButtonWeight; size?: ButtonSize }) {
  return <button className={buttonClass(weight, size, className)} {...props} />;
}

export function ButtonLink({
  weight = 'primary',
  size = 'md',
  className,
  ...props
}: ComponentProps<typeof Link> & { weight?: ButtonWeight; size?: ButtonSize }) {
  return <Link className={buttonClass(weight, size, className)} {...props} />;
}

/* -------------------------------------------------------------------------- */
/* Surfaces                                                                    */
/* -------------------------------------------------------------------------- */

export function Panel({
  className,
  children,
  ...props
}: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'rounded-[10px] border border-line bg-panel shadow-[var(--shadow-1)]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SectionHeading({
  title,
  sub,
  action,
  as: As = 'h2',
}: {
  title: string;
  sub?: string;
  action?: ReactNode;
  as?: 'h1' | 'h2' | 'h3';
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <As className="text-2xl font-bold tracking-[-0.02em] sm:text-[28px]">{title}</As>
        {sub && <p className="mt-1 max-w-[60ch] text-[15px] text-muted">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

type Tone = 'neutral' | 'brand' | 'ok' | 'warn' | 'danger';

/**
 * Ink on tint, with no border.
 *
 * The sheet's contrast ratios are measured on exactly this pairing: 6.8:1 for
 * success, 6.4:1 warning, 6.7:1 danger, in both themes. The old map used the
 * FULL colour as text on the tint, which is a different and weaker pair, and it
 * added a border that made every pill shout. Status is the only colour in the
 * back office now, so each one has to earn it. R-187.
 */
const TONE: Record<Tone, string> = {
  neutral: 'bg-raised text-muted',
  brand: 'bg-brand-wash text-brand-ink',
  ok: 'bg-ok-wash text-[color:var(--color-ok-ink)]',
  warn: 'bg-warn-wash text-[color:var(--color-warn-ink)]',
  danger: 'bg-danger-wash text-[color:var(--color-danger-ink)]',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-5 items-center gap-1.5 rounded-[4px] px-2 text-[11px] font-medium',
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Status is never carried by colour alone: every tone ships a word. */
export function Callout({
  tone = 'warn',
  title,
  children,
  className,
}: {
  tone?: Tone;
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-[6px] border border-current/20 px-4 py-3 text-[13px]',
        TONE[tone],
        className,
      )}
      role={tone === 'danger' ? 'alert' : undefined}
    >
      <p className="font-semibold">{title}</p>
      {children && <div className="mt-1 leading-relaxed opacity-90">{children}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Form fields                                                                 */
/* -------------------------------------------------------------------------- */

export const inputClass =
  // 32px tall, 6px radius, and the focus ring is ink rather than brand: the
  // yellow is storefront decoration now and could not carry a focus ring on
  // paper anyway. Two pixels, two pixels of offset, on every control.
  'h-8 w-full rounded-[6px] border border-line-strong bg-panel px-3 text-[13px] text-ink ' +
  'placeholder:text-faint focus:border-[color:var(--color-ink)] focus:outline-2 ' +
  'focus:outline-offset-2 focus:outline-[color:var(--color-control)]';

/**
 * The same control, for a box somebody writes paragraphs in.
 *
 * `inputClass` pins `h-8`, which is right for a text field and silently
 * defeats `rows` on a textarea: the procedure editor rendered as a single
 * 32px line with twelve rows of text scrolling inside it. A textarea needs its
 * height from its content, so the height rule is the one thing that differs.
 */
export const textareaClass = inputClass
  .replace('h-8 ', '')
  .concat(' min-h-[9rem] py-2 leading-[1.5]');

export function Field({
  label,
  hint,
  error,
  optional,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-sm font-semibold">
        {label}
        {optional && <span className="ml-1.5 font-normal text-muted">(optional)</span>}
      </label>
      {/* Hint above the field: keeps downward momentum and never gets covered
          by the browser's autofill dropdown. */}
      {hint && <p className="-mt-1 text-[13px] text-muted">{hint}</p>}
      {error && (
        <p className="-mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-danger">
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
            <path d="M8 1.5 15 14H1L8 1.5Zm0 4.2a.8.8 0 0 0-.8.8v2.8a.8.8 0 0 0 1.6 0V6.5a.8.8 0 0 0-.8-.8Zm0 5.6a.9.9 0 1 0 0 1.8.9.9 0 0 0 0-1.8Z" />
          </svg>
          {error}
        </p>
      )}
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty state                                                                 */
/* -------------------------------------------------------------------------- */

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-[10px] border border-dashed border-line px-6 py-12 text-center">
      <p className="text-[15px] font-semibold">{title}</p>
      <p className="mx-auto mt-2 max-w-[46ch] text-[13px] text-muted">{body}</p>
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

/** Skeleton shaped like the thing it replaces, never a spinner. */
export function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
      <div className="aspect-square animate-pulse bg-raised" />
      <div className="space-y-2 p-4">
        <div className="h-3 w-20 animate-pulse rounded bg-raised" />
        <div className="h-4 w-4/5 animate-pulse rounded bg-raised" />
        <div className="h-4 w-16 animate-pulse rounded bg-raised" />
      </div>
    </div>
  );
}
